import { useState, useMemo } from 'react';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TimeSlot, Availability, Participant } from '../../types';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { AllSlotsDialog } from './AllSlotsDialog';
import { HeroSlotCard } from './HeroSlotCard';
import { AltSlotCard } from './AltSlotCard';

interface TimeSlotLeaderboardProps {
  roomId: string | undefined;
  participantName: string | null;
  availability: Availability[];
  participants?: Participant[];
  minDuration: number;
  onSlotSelect: (slot: TimeSlot) => Promise<void>;
}

export function TimeSlotLeaderboard({
  roomId,
  participantName,
  availability,
  participants = [],
  minDuration,
  onSlotSelect,
}: TimeSlotLeaderboardProps) {
  const { t } = useTranslation();
  const [expandedSlotKey, setExpandedSlotKey] = useState<string | null>(null);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [selectingSlot, setSelectingSlot] = useState<string | null>(null);

  const { slots, isLoading } = useLeaderboard({
    roomId,
    minDuration,
    availability,
  });

  const allParticipantNames = useMemo(() => {
    if (participants.length > 0) return participants.map(p => p.name);
    const set = new Set<string>();
    availability.forEach(a => set.add(a.participant));
    return Array.from(set);
  }, [participants, availability]);

  const totalParticipants = allParticipantNames.length;
  const topSlot = slots[0];
  const altSlots = slots.slice(1, 3);
  const hasMoreSlots = slots.length > 3;
  const hasMarkedSlots = useMemo(
    () => availability.some(a => a.available),
    [availability]
  );

  const slotKey = (slot: TimeSlot) => `${slot.utc_start}-${slot.utc_end}`;

  const toggleExpand = (key: string) => {
    setExpandedSlotKey(prev => (prev === key ? null : key));
  };

  const handleSelectSlot = async (slot: TimeSlot) => {
    if (!participantName) return;
    const key = slotKey(slot);
    setSelectingSlot(key);
    try {
      await onSlotSelect(slot);
    } finally {
      setSelectingSlot(null);
    }
  };

  if (!roomId) return null;

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-6 w-6 animate-spin text-room-accent" />
        </div>
      ) : slots.length === 0 ? (
        <div className="rounded-xl border border-dashed border-room-border px-4 py-10 text-center">
          <p className="text-sm font-medium text-room-text">
            {hasMarkedSlots ? t('leaderboard.noSlotsFound') : t('leaderboard.markTimesFirst')}
          </p>
          {hasMarkedSlots ? (
            <p className="mt-1 text-xs text-room-text-dim">{t('leaderboard.adjustFilter')}</p>
          ) : null}
        </div>
      ) : (
        <>
          {topSlot ? (
            <>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-room-text-faint">
                {t('leaderboard.topPick', { defaultValue: 'Top pick' })}
              </p>
              <HeroSlotCard
                slot={topSlot}
                totalParticipants={totalParticipants}
                allParticipantNames={allParticipantNames}
                participantName={participantName}
                isOpen={expandedSlotKey === slotKey(topSlot)}
                onToggle={() => toggleExpand(slotKey(topSlot))}
              />
            </>
          ) : null}

          {altSlots.length > 0 ? (
            <>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-room-text-faint">
                {t('leaderboard.otherOptions', { defaultValue: 'Other good options' })}
              </p>
              <div className="mb-2.5 flex flex-col gap-2">
                {altSlots.map((slot, index) => {
                  const key = slotKey(slot);
                  return (
                    <AltSlotCard
                      key={key}
                      slot={slot}
                      rank={index + 2}
                      totalParticipants={totalParticipants}
                      allParticipantNames={allParticipantNames}
                      participantName={participantName}
                      availability={availability}
                      isSelecting={selectingSlot === key}
                      isOpen={expandedSlotKey === key}
                      onToggle={() => toggleExpand(key)}
                      onSelect={handleSelectSlot}
                    />
                  );
                })}
              </div>
            </>
          ) : null}

          {hasMoreSlots ? (
            <button
              type="button"
              onClick={() => setShowAllSlots(true)}
              className="mb-7 w-full rounded-[14px] border border-dashed border-room-border-strong bg-transparent py-3 text-[13.5px] font-semibold text-room-accent hover:bg-room-accent-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent"
            >
              {t('leaderboard.viewMore', { count: slots.length - 3 })}
            </button>
          ) : null}
        </>
      )}

      {showAllSlots ? (
        <AllSlotsDialog
          slots={slots}
          participantName={participantName}
          availability={availability}
          allParticipantNames={allParticipantNames}
          expandedSlotKey={expandedSlotKey}
          onToggleExpand={toggleExpand}
          onClose={() => setShowAllSlots(false)}
          onSelectSlot={handleSelectSlot}
          selectingSlot={selectingSlot}
        />
      ) : null}
    </>
  );
}
